import { EllipsisVertical } from 'lucide-react'
import { actionTone } from './actionTone'
import IconButton from './IconButton'
import Menu, { MenuItem } from './Menu'
import { kit } from './strings'
import './RowActions.css'

/**
 * The actions belonging to one table row.
 *
 * Six unlabelled glyphs in a row is not a set of choices, it is a wall: nothing
 * says which one you reach for, every row repeats the wall, and the column has
 * to be 230px wide to hold them — wide enough to push the phishing campaigns
 * list into horizontal scroll at 1440px, with the action column itself first
 * off the screen.
 *
 * So the first `inline` actions stay as glyphs and the rest fold into a kebab.
 * Nothing is removed: everything in the overflow keeps its icon AND gains a
 * visible Arabic label, which the tooltip-only glyphs never had.
 *
 * Folding one action is not worth a kebab that costs the same width, so a set
 * that would leave exactly one in the menu renders whole — with three inline (the
 * default since the chips, 2026-10-19), a row of up to four shows every action.
 *
 * An action can BE A LINK rather than a handler: `as: 'a'` with an `href` opens a
 * stored file, `as: Link` with a `to` navigates. Both keep the middle-click, the
 * right-click menu and the status bar a real link gives, which `window.open` in an
 * onClick throws away.
 *
 * @param {{id: string, icon: import('react').ElementType, label: string,
 *          tone?: string, onClick?: () => void, as?: import('react').ElementType,
 *          to?: string, href?: string, target?: string, rel?: string,
 *          danger?: boolean, disabled?: boolean}[]} actions  in priority order
 * @param {number} [inline]  how many stay as glyphs
 * @param {string} [menuLabel]  accessible name for the kebab
 */
export default function RowActions({ actions, inline = 3, menuLabel = kit.options }) {
  const items = actions.filter(Boolean)
  const folded = items.length > inline + 1 ? items.slice(inline) : []
  const shown = folded.length ? items.slice(0, inline) : items

  return (
    <span className="row-actions">
      {shown.map(({ id, icon, label, tone, danger, ...rest }) => (
        <IconButton
          key={id}
          icon={icon}
          label={label}
          tone={danger ? 'danger' : tone}
          {...rest}
        />
      ))}

      {folded.length > 0 && (
        <Menu
          align="end"
          trigger={({ toggle, open, id }) => (
            <IconButton
              icon={EllipsisVertical}
              label={menuLabel}
              /* Neutral. It opens a list; it does not do anything, and it should not
                 borrow the colour of something that does. */
              tone="primary"
              onClick={toggle}
              aria-haspopup="true"
              aria-expanded={open}
              aria-controls={id}
              className={open ? 'is-open' : undefined}
            />
          )}
        >
          {/* `disabled` TRAVELS WITH THE ACTION. An action that is unavailable on this row
              stays unavailable once it folds — without this it kept its greyed-out glyph
              inline and became a live menu row the moment a kebab appeared. */}
          {/* The tone travels into the menu too: an action that folds keeps the colour it
              had inline, so the same act is the same colour wherever it is drawn. */}
          {folded.map(({ id, icon, label, danger, tone, onClick, disabled, as, to, href, target, rel }) => (
            <MenuItem
              key={id}
              icon={icon}
              tone={danger ? 'danger' : actionTone(icon, tone ?? 'brand')}
              danger={danger || tone === 'danger'}
              onClick={onClick}
              disabled={disabled}
              {...(as ? { as, to, href, target, rel } : null)}
            >
              {label}
            </MenuItem>
          ))}
        </Menu>
      )}
    </span>
  )
}
